// Host availability calendar (Part B): READ-ONLY occupancy view over the
// host's OWN listings. Data comes exclusively from the finalized
// GET /api/spots/{id}/availability endpoint — slot states are never inferred
// from cached bookings, and this screen cannot create or mutate anything.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, CalendarClock, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading, EmptyState } from "../components/States";
import { Calendar } from "../components/ui/calendar";
import { calendarClassNames } from "../components/DateTimeFields";
import { api } from "../lib/api";
import { setPageTitle } from "../lib/format";
import { toDateStr } from "../lib/scheduler";

// Host semantics (Part B8): available → GREEN, held → BLUE, booked → RED.
// Color is never the only signal — every chip carries text + aria-label.
const STATE_STYLE = {
  available: { chip: "border-emerald-500/30 bg-emerald-500/8", dot: "bg-emerald-400", text: "text-emerald-300", label: "Available" },
  held: { chip: "border-sky-500/30 bg-sky-500/8", dot: "bg-sky-400", text: "text-sky-300", label: "Held" },
  booked: { chip: "border-red-500/30 bg-red-500/8", dot: "bg-red-500", text: "text-red-300", label: "Booked" },
};
const UNKNOWN_STYLE = { chip: "border-[#27272A] bg-[#1F2328]/40", dot: "bg-zinc-600", text: "text-zinc-500", label: "Unavailable" };

// Slot start/end are listing-local wall-clock ISO (no suffix) — format the wall
// clock verbatim, never through a timezone conversion.
const fmtSlot = (iso) => {
  const [h, m] = iso.slice(11, 16).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const tzAbbrev = (tz, date) => {
  try {
    const noonUtc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12));
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(noonUtc).find((p) => p.type === "timeZoneName");
    return part?.value || "";
  } catch { return ""; }
};

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function HostAvailability() {
  const [listings, setListings] = useState(null);
  const [listingsError, setListingsError] = useState(null);
  const [spotId, setSpotId] = useState(null);
  const [day, setDay] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const reqSeq = useRef(0);

  useEffect(() => { setPageTitle("Availability"); }, []);

  useEffect(() => {
    api.mySpots()
      .then((rows) => { setListings(rows); if (rows.length) setSpotId((cur) => cur || rows[0].spot_id); })
      .catch((e) => { setListingsError(e.message); setListings([]); });
  }, []);

  const spot = useMemo(() => listings?.find((s) => s.spot_id === spotId) || null, [listings, spotId]);

  const load = useCallback(async () => {
    if (!spotId) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    const start = `${toDateStr(day)}T00:00:00`;
    const end = `${toDateStr(addDays(day, 1))}T00:00:00`;
    try {
      const r = await api.spotAvailability(spotId, start, end);
      if (seq !== reqSeq.current) return; // stale response — a newer request won
      setData(r);
      setUpdatedAt(new Date());
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e.message);
      setData(null);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [spotId, day]);

  useEffect(() => { load(); }, [load]);

  // Refresh on window focus / tab return (Part B13) — no aggressive polling.
  useEffect(() => {
    const onWake = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [load]);

  const counts = useMemo(() => {
    const c = { available: 0, held: 0, booked: 0 };
    (data?.slots || []).forEach((s) => { if (c[s.status] != null) c[s.status] += 1; });
    return c;
  }, [data]);

  const dayLabel = day.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="min-h-screen flex flex-col" data-testid="host-availability-page">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Hosting</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="type-display">Availability</h1>
          {spot && (
            <p className="text-xs text-zinc-500" data-testid="host-tz-label">
              <CalendarClock className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-[#FF3B45]" aria-hidden="true" />
              Timezone: <span className="text-zinc-300 font-semibold">{data?.listing_timezone || spot.timezone || "—"}</span>
              {data?.listing_timezone && (
                <span className="text-zinc-600"> · {dayLabel} · {tzAbbrev(data.listing_timezone, day)}</span>
              )}
            </p>
          )}
        </div>

        {/* Listing selector (Part B4) — only listings the server returns for THIS host */}
        <div className="mt-8">
          <p className="meta-label mb-3">My listings</p>
          {listings === null ? (
            <div className="flex gap-2">{[0, 1].map((i) => <div key={i} className="h-10 w-44 rounded-xl bg-[#131519] animate-pulse" />)}</div>
          ) : listingsError ? (
            <EmptyState title="Unable to load your listings" body={listingsError} />
          ) : listings.length === 0 ? (
            <EmptyState title="No listings yet" body="Add a parking space first — its availability calendar will appear here." />
          ) : (
            <div className="flex flex-wrap gap-2" role="listbox" aria-label="My listings" data-testid="host-listing-selector">
              {listings.map((s) => (
                <button key={s.spot_id} role="option" aria-selected={s.spot_id === spotId}
                        onClick={() => setSpotId(s.spot_id)}
                        data-testid={`host-listing-option-${s.spot_id}`}
                        className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E50914] ${
                          s.spot_id === spotId
                            ? "bg-[#E50914] border-[#E50914] text-white shadow-[0_0_24px_rgba(229,9,20,0.3)]"
                            : "bg-[#0D1114]/70 border-[#27272A] text-zinc-400 hover:border-[#3F3F46] hover:text-zinc-200"}`}>
                  {s.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {spot && (
          <div className="mt-8 grid lg:grid-cols-[auto_1fr] gap-6 items-start">
            {/* Calendar — glass surface */}
            <div className="rounded-2xl border border-[#27272A] bg-[#0D1114]/70 backdrop-blur-xl p-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)] w-fit mx-auto lg:mx-0"
                 data-testid="host-availability-calendar">
              <Calendar
                mode="single"
                selected={day}
                onSelect={(d) => d && setDay(d)}
                defaultMonth={day}
                classNames={calendarClassNames}
              />
            </div>

            {/* Day slots */}
            <div className="rounded-2xl border border-[#27272A] bg-[#0D1114]/70 backdrop-blur-xl p-6 shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setDay((d) => addDays(d, -1))} data-testid="host-avail-prev-day" aria-label="Previous day"
                          className="h-9 w-9 rounded-lg border border-[#27272A] text-zinc-400 hover:text-white hover:border-[#3F3F46] transition-colors flex items-center justify-center">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h2 className="type-h2 min-w-40 text-center" data-testid="host-avail-day">{dayLabel}</h2>
                  <button onClick={() => setDay((d) => addDays(d, 1))} data-testid="host-avail-next-day" aria-label="Next day"
                          className="h-9 w-9 rounded-lg border border-[#27272A] text-zinc-400 hover:text-white hover:border-[#3F3F46] transition-colors flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <button onClick={load} data-testid="host-avail-refresh" aria-label="Refresh availability"
                        className="flex items-center gap-2 px-3.5 h-9 rounded-lg border border-[#27272A] text-xs font-semibold text-zinc-400 hover:text-white hover:border-[#3F3F46] transition-colors">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>

              {/* Legend (Part B5) — color + text, always visible */}
              <div className="mt-5 flex flex-wrap items-center gap-4" data-testid="host-legend" aria-label="Legend">
                {Object.entries(STATE_STYLE).map(([k, s]) => (
                  <span key={k} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                    <span className={`w-2 h-2 rounded-full ${s.dot}`} aria-hidden="true" />{s.label}
                  </span>
                ))}
                {data && (
                  <span className="ml-auto text-[11px] text-zinc-600" data-testid="host-avail-summary" aria-live="polite">
                    {counts.available} open · {counts.held} held · {counts.booked} booked
                    {updatedAt && ` · updated ${updatedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
                  </span>
                )}
              </div>

              <div className="mt-5" data-testid="host-availability-slots">
                {loading && !data ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="h-11 rounded-xl bg-[#131519] animate-pulse" />
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-10" data-testid="host-availability-error">
                    <p className="text-sm text-red-300">Unable to load availability.</p>
                    <p className="text-xs text-zinc-600 mt-1">{error}</p>
                    <button onClick={load} data-testid="host-avail-retry" className="btn-secondary !px-4 !py-2 text-xs mt-4">Retry</button>
                  </div>
                ) : !data || data.slots.length === 0 ? (
                  <div className="text-center py-10" data-testid="host-availability-empty">
                    <p className="text-sm text-zinc-400">No availability data for this date.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2" role="list" aria-label="Time slots">
                    {data.slots.map((s) => {
                      const st = STATE_STYLE[s.status] || UNKNOWN_STYLE; // unknown states never read as available
                      const label = `${fmtSlot(s.start)} — ${st.label}`;
                      return (
                        <div key={s.start} role="listitem" title={label} aria-label={label}
                             data-testid={`host-slot-${s.start.slice(11, 16).replace(":", "")}`}
                             data-status={s.status}
                             className={`flex items-center gap-2.5 h-11 px-3.5 rounded-xl border text-sm font-semibold transition-colors ${st.chip} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${st.dot}`} aria-hidden="true" />
                          <span className="truncate">{fmtSlot(s.start)}</span>
                          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider opacity-70">{st.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="mt-5 flex items-center gap-1.5 text-[11px] text-zinc-600">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FF3B45]" aria-hidden="true" />
                Read-only view — bookings and holds can only change through the checkout flow.
              </p>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
